import { escapeHtml, safeUrl } from '../escape.js';
import { editAttr, itemStr, list, sectionOpen, str } from './helpers.js';
import type { JsonValueT, PatternSectionT } from '../../schema/index.js';

const COLUMN_OPEN = '    <div class="ve-footer__column">';
const COLUMN_CLOSE = '    </div>';

function renderLinks(section: PatternSectionT, key: string, items: JsonValueT[]): string {
  return items
    .map((item, index) => {
      const label = itemStr(item, 'label');
      const url = itemStr(item, 'url');
      if (!label || !url) return '';
      const itemPath = `/${key}/${index}`;
      return `        <li class="ve-footer__link"${editAttr(section, itemPath)}><a href="${safeUrl(url)}">${escapeHtml(label)}</a></li>`;
    })
    .filter(Boolean)
    .join('\n');
}

function renderLinkColumn(heading: string, headingPath: string, links: string): string {
  if (!heading && !links) return '';
  const parts = [COLUMN_OPEN];
  if (heading) parts.push(`      <h3 class="ve-footer__column-heading"${headingPath}>${escapeHtml(heading)}</h3>`);
  if (links) parts.push(`      <ul class="ve-footer__links">\n${links}\n      </ul>`);
  parts.push(COLUMN_CLOSE);
  return parts.join('\n');
}

function renderBusinessColumn(section: PatternSectionT, businessName: string, description: string): string {
  if (!businessName && !description) return '';
  const parts = [COLUMN_OPEN];
  if (businessName) parts.push(`      <h2 class="ve-footer__business"${editAttr(section, '/business_name')}>${escapeHtml(businessName)}</h2>`);
  if (description) parts.push(`      <p class="ve-footer__description"${editAttr(section, '/business_description')}>${escapeHtml(description)}</p>`);
  parts.push(COLUMN_CLOSE);
  return parts.join('\n');
}

function renderContactColumn(section: PatternSectionT, phone: string, email: string, address: string): string {
  if (!phone && !email && !address) return '';
  const contactHeading = str(section.fields, 'contact_heading');
  const parts = [COLUMN_OPEN];
  if (contactHeading) parts.push(`      <h3 class="ve-footer__column-heading"${editAttr(section, '/contact_heading')}>${escapeHtml(contactHeading)}</h3>`);
  if (phone) {
    const phoneUrl = `tel:${phone}`;
    parts.push(`      <a class="ve-footer__phone" href="${safeUrl(phoneUrl)}"${editAttr(section, '/phone')}>${escapeHtml(phone)}</a>`);
  }
  if (email) {
    const emailUrl = `mailto:${email}`;
    parts.push(`      <a class="ve-footer__email" href="${safeUrl(emailUrl)}"${editAttr(section, '/email')}>${escapeHtml(email)}</a>`);
  }
  if (address) parts.push(`      <address class="ve-footer__address"${editAttr(section, '/address')}>${escapeHtml(address)}</address>`);
  parts.push(COLUMN_CLOSE);
  return parts.join('\n');
}

function renderColumns(section: PatternSectionT, businessName: string): string {
  const { fields } = section;
  const description = str(fields, 'business_description');
  const phone = str(fields, 'phone');
  const email = str(fields, 'email');
  const address = str(fields, 'address');
  const quickHeading = str(fields, 'quick_links_heading');
  const socialHeading = str(fields, 'social_heading');
  const navLinks = renderLinks(section, 'nav_links', list(fields, 'nav_links'));
  const socialLinks = renderLinks(section, 'social_links', list(fields, 'social_links'));
  if (!description && !navLinks && !phone && !email && !address && !socialLinks) return '';

  return [
    '  <div class="ve-footer__columns">',
    renderBusinessColumn(section, businessName, description),
    renderLinkColumn(quickHeading, editAttr(section, '/quick_links_heading'), navLinks),
    renderContactColumn(section, phone, email, address),
    renderLinkColumn(socialHeading, editAttr(section, '/social_heading'), socialLinks),
    '  </div>',
  ]
    .filter(Boolean)
    .join('\n');
}

function renderAttribution(section: PatternSectionT, businessName: string, year: string): string {
  if (!businessName && !year) return '';
  return `  <p class="ve-footer__attribution">© <span class="ve-footer__year"${editAttr(section, '/year')}>${escapeHtml(year)}</span> <span class="ve-footer__name"${editAttr(section, '/business_name')}>${escapeHtml(businessName)}</span></p>`;
}

/** footer — attribution or multi-column business details; heading is intentionally ignored. */
export function renderFooter(section: PatternSectionT): string {
  const businessName = str(section.fields, 'business_name');
  const year = str(section.fields, 'year');
  return [sectionOpen(section), renderColumns(section, businessName), renderAttribution(section, businessName, year), '</section>']
    .filter(Boolean)
    .join('\n');
}
